import DataProvider from './DataProvider';
import platformConfig from '../config/platformConfig';

/**
 * WizardProvider — single synthetic project for project-wizard.html preview.
 *
 * Generates deterministic fake token hashes for grid preview.
 * Script comes directly from wizard editor via URL params or postMessage.
 */
class WizardProvider extends DataProvider {
  constructor(options = {}) {
    super();
    this.projectName = options.projectName || 'Preview Project';
    this.artistName = options.artistName || 'Artist';
    this.description = options.description || '';
    this.maxEditions = options.maxEditions || 10;
    this.maxGridDimension = options.maxGridDimension || 4;
    this.scriptType = options.scriptType || 'p5js';
    this.scriptContent = options.script || '';
    this.cssContent = options.css || null;
    this.infoContent = options.infoPost || '';
    this._tokens = null;
  }

  get sourceType() { return 'wizard'; }
  get isReadOnly() { return true; }

  async initialize() {
    return { success: true, needsModal: null };
  }

  async getPlatformConfig() {
    return {
      platform: { ...platformConfig },
      maxTotalSupply: 0,
      currentTokenId: this.maxEditions,
      currentProjectId: 1,
      artistPercent: 9000,
      immutablesWEB: "",
      immutablesURI: "",
      useMetadataServer: false,
      curator: null,
      curatorPercent: null,
      beneficiary: null,
      beneficiaryPercent: null,
      projectFeeInWei: "0",
      projectFeeInEth: "0",
      featuredProjectId: 1,
      secondaryRoyaltyPercent: 1000,
    };
  }

  async getUserRoles() {
    return {
      userIsAdmin: false,
      userIsContractOwner: false,
      userIsPayee: false,
      userIsAuthorizedArtist: true,
      userIsProjectArtist: true,
      artistScreeningEnabled: false,
      contractOwner: null,
    };
  }

  async getProjects() {
    const savedTokens = this._loadTokens();
    const project = {
      name: this.projectName,
      artist: this.artistName,
      description: this.description,
      currentEditionId: String(savedTokens.length),
      maxEditions: String(this.maxEditions),
      maxGridDimension: String(this.maxGridDimension),
      scriptTransactionHash: '0x0000000000000000000000000000000000000000000000000000000000000000',
      scriptType: this.scriptType,
      category: 'Preview',
      active: true,
      paused: false,
      locked: false,
    };

    return {
      projects: [project],
      artists: ['0x0000000000000000000000000000000000000000'],
    };
  }

  async getProjectDetails(projectId) {
    return {
      projectArtistAddress: '0x0000000000000000000000000000000000000000',
      projectPricePerEditionInWei: "0",
      projectPricePerEditionInEth: "0",
      projectAdditionalPayee: null,
      projectAdditionalPayeePercent: null,
      projectRoyaltyAddress: '0x0000000000000000000000000000000000000000',
      projectIdToImageURLBase: null,
      projectIdToImageURLExt: null,
      projectIdUseImageURLInGridView: false,
      clientViewImageURLInDetailView: false,
      royaltyContractBalance: "0",
    };
  }

  async getProjectTokens(projectId, currentEditions, onProgress) {
    if (!this._tokens) this._tokens = this._loadTokens();
    return this._tokens;
  }

  /**
   * Mint a new edition in memory. Returns the new token or null if sold out.
   * @param {number} [maxEditions] — override from current project state
   */
  mintEdition(maxEditions) {
    if (!this._tokens) this._tokens = this._loadTokens();
    const limit = maxEditions != null ? maxEditions : this.maxEditions;
    const editionNum = this._tokens.length + 1;
    if (editionNum > limit) return null;
    const token = {
      networkId: 0,
      blockNumber: 0,
      projectId: "1",
      editionId: String(editionNum),
      tokenId: String(editionNum),
      transactionHash: this._randomHash(),
    };
    this._tokens.push(token);
    this._saveTokens();
    return token;
  }

  _storageKey() {
    return `wizardMints_${this.projectName}`;
  }

  _loadTokens() {
    try {
      const stored = localStorage.getItem(this._storageKey());
      if (stored) return JSON.parse(stored);
    } catch (e) { /* ignore */ }
    return [];
  }

  _saveTokens() {
    try {
      localStorage.setItem(this._storageKey(), JSON.stringify(this._tokens));
    } catch (e) { /* ignore */ }
  }

  async getProjectScript(scriptTransactionHash) {
    return {
      script: this.scriptContent,
      poster: null,
      page: this.projectName,
      blockNumber: null,
    };
  }

  async getPagePosts(pageName, scriptBlockNumber) {
    if (!this.infoContent) return [];
    return [{
      poster: null,
      page: this.projectName,
      tag: 'info.immutables.art',
      content: this.infoContent,
      blockNumber: null,
      transactionHash: null,
    }];
  }

  async getOwnerOfToken(tokenId) {
    return null;
  }

  async getEditionEventHistory(editionId, projectTokens) {
    return [];
  }

  async getTeammembers() {
    return {};
  }

  async getAuthorizedArtists() {
    return {};
  }

  async getContractBalance() {
    return { contractBalance: "0", contractBalanceWei: "0" };
  }

  async getProjectCSS(imageURLBase, isMobile, projectId) {
    return this.cssContent;
  }

  /**
   * Update the script content (called from wizard postMessage).
   */
  updateScript(script) {
    this.scriptContent = script;
    this._tokens = null; // force regeneration if script changes token generation
  }

  /**
   * Generate a random transaction hash for preview tokens.
   */
  _randomHash() {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return '0x' + hex;
  }
}

export default WizardProvider;
